import { inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import type { ApiResponse, PaginatedApiResponse } from '../../responses/api.response';

// Re-exportar interfaces para compatibilidad
export type { ApiResponse, PaginatedApiResponse } from '../../responses/api.response';

/**
 * Base abstract service to centralize HTTP access.
 * Extend this class in feature services (e.g., AuthService) to reuse
 * typed helpers and consistent URL building against environment.apiUrl.
 */
export abstract class BaseService {
    protected readonly http = inject(HttpClient);
    protected readonly apiUrl = environment.apiUrl;

    protected url(path: string): string {
        const clean = path.replace(/^\/+/, '');
        return `${this.apiUrl}/${clean}`;
    }

    /**
     * Generic helpers returning raw type T (can be ApiResponse<R> or any typed shape)
     * T is the response type, which can be ApiResponse<R> or any other shape
     */
    protected get<T = unknown>(
        path: string,
        options?: {
            params?: Record<string, string | number | boolean | readonly (string | number | boolean)[]> | HttpParams;
            headers?: HttpHeaders | Record<string, string>;
        }
    ): Observable<T> {
        const httpOptions = this.toHttpOptions(options);
        httpOptions.withCredentials = true;
        return this.http.get<T>(this.url(path), httpOptions).pipe(catchError(this.handleError));
    }

    protected post<T = unknown>(
        path: string,
        body: unknown,
        options?: {
            params?: Record<string, string | number | boolean | readonly (string | number | boolean)[]> | HttpParams;
            headers?: HttpHeaders | Record<string, string>;
        }
    ): Observable<T> {
        const httpOptions = this.toHttpOptions(options);
        httpOptions.withCredentials = true;
        return this.http.post<T>(this.url(path), body, httpOptions).pipe(catchError(this.handleError));
    }

    protected put<T = unknown>(
        path: string,
        body: unknown,
        options?: {
            params?: Record<string, string | number | boolean | readonly (string | number | boolean)[]> | HttpParams;
            headers?: HttpHeaders | Record<string, string>;
        }
    ): Observable<T> {
        const httpOptions = this.toHttpOptions(options);
        httpOptions.withCredentials = true;
        return this.http.put<T>(this.url(path), body, httpOptions).pipe(catchError(this.handleError));
    }

    protected patch<T = unknown>(
        path: string,
        body: unknown,
        options?: {
            params?: Record<string, string | number | boolean | readonly (string | number | boolean)[]> | HttpParams;
            headers?: HttpHeaders | Record<string, string>;
        }
    ): Observable<T> {
        const httpOptions = this.toHttpOptions(options);
        httpOptions.withCredentials = true;
        return this.http.patch<T>(this.url(path), body, httpOptions).pipe(catchError(this.handleError));
    }

    protected delete<T = unknown>(
        path: string,
        options?: {
            params?: Record<string, string | number | boolean | readonly (string | number | boolean)[]> | HttpParams;
            headers?: HttpHeaders | Record<string, string>;
        }
    ): Observable<T> {
        const httpOptions = this.toHttpOptions(options);
        httpOptions.withCredentials = true;
        return this.http.delete<T>(this.url(path), httpOptions).pipe(catchError(this.handleError));
    }

    /**
     * Convenience helper to map an ApiResponse<T> to T (data), if desired by consumers.
     */
    protected unwrap<T>(obs: Observable<ApiResponse<T>>): Observable<T> {
        return obs as unknown as Observable<T>; // consumer may use rxjs map; keep minimal to not change behavior
    }

    private toHttpOptions(options?: { params?: any; headers?: any }) {
        const httpOptions: { params?: HttpParams; headers?: HttpHeaders; withCredentials?: boolean } = {};

        if (options?.params) {
            httpOptions.params = options.params instanceof HttpParams ? options.params : new HttpParams({ fromObject: this.toStringRecord(options.params) });
        }

        if (options?.headers) {
            httpOptions.headers = options.headers instanceof HttpHeaders ? options.headers : new HttpHeaders(options.headers as Record<string, string>);
        }

        return httpOptions;
    }

    private toStringRecord(obj: Record<string, unknown>): Record<string, string> {
        const out: Record<string, string> = {};
        for (const [k, v] of Object.entries(obj)) {
            if (Array.isArray(v)) {
                out[k] = v.join(',');
            } else if (v === null || v === undefined) {
                // skip
            } else {
                out[k] = String(v);
            }
        }
        return out;
    }

    // Basic error pass-through; interceptors can enrich/handle globally
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private handleError = (error: any) => {
        return throwError(() => error);
    };
}
